import { getGlobalHeadersAsObject } from "./global-headers";

/** Flow metrics for one message queue — see core/endpoints/queue_health.py. */
export interface QueueFlowHealth {
	/** Queued, unsent, no error. */
	backlog: number;
	/** Of those, the ones past the cutoff — what `?stuck=true` returns. */
	stuck: number;
	sent_1h: number;
	sent_24h: number;
	sent_window: number;
	window_days: number;
	last_sent_at: string | null;
	idle_hours: number | null;
	oldest_backlog_at: string | null;
	oldest_backlog_hours: number | null;
	/** Hours for the backlog to clear at the queue's own average rate. */
	drain_hours: number | null;
	/** Rows waiting and nothing sent in 24h. */
	stalled: boolean;
	/** Queue is moving, but its oldest row waited far longer than throughput explains. */
	aging: boolean;
	alert: boolean;
}

export interface ExtractionHealthCheck {
	metric: string;
	value_1h: number;
	value_24h: number;
	floor_1h: number;
	floor_24h: number;
	alert_1h: boolean;
	alert_24h: boolean;
	alert: boolean;
}

export interface ExtractionHealth {
	healthy: boolean;
	checks: ExtractionHealthCheck[];
}

const fetchJson = async <T>(path: string): Promise<T> => {
	const response = await fetch(path, {
		headers: { ...getGlobalHeadersAsObject() },
	});
	if (!response.ok) {
		throw new Error(`Failed to fetch ${path}: ${response.statusText}`);
	}
	return response.json();
};

export const fetchEmailQueueHealth = (): Promise<QueueFlowHealth> =>
	fetchJson<QueueFlowHealth>("/api/v2/queue-health/email/");

export const fetchLinkedinQueueHealth = (): Promise<QueueFlowHealth> =>
	fetchJson<QueueFlowHealth>("/api/v2/queue-health/linkedin/");

export const fetchExtractionHealth = (): Promise<ExtractionHealth> =>
	fetchJson<ExtractionHealth>("/api/v2/queue-health/extraction/");

export interface CeleryErrorGroup {
	task_name: string;
	count: number;
	exception: string;
}

export interface CeleryErrorsHealth {
	unsent: number;
	threshold: number;
	groups: CeleryErrorGroup[];
}

export const fetchCeleryErrorsHealth = (): Promise<CeleryErrorsHealth> =>
	fetchJson<CeleryErrorsHealth>("/api/v2/queue-health/celery-errors/");

export interface PluginWorkLogHealth {
	errors: number;
	stuck: number;
	threshold: number;
}

export const fetchPluginWorkLogHealth = (): Promise<PluginWorkLogHealth> =>
	fetchJson<PluginWorkLogHealth>("/api/v2/queue-health/plugin-work-log/");

export interface DbBackupHealth {
	age_hours: number | null;
	threshold_hours: number;
	alert: boolean;
}

export const fetchDbBackupHealth = (): Promise<DbBackupHealth> =>
	fetchJson<DbBackupHealth>("/api/v2/queue-health/db-backup/");

export interface EsIndexHealth {
	/** Index names that do not exist in this environment. */
	missing: string[];
	es_enabled: boolean;
	alert: boolean;
}

export const fetchEsIndexHealth = (): Promise<EsIndexHealth> =>
	fetchJson<EsIndexHealth>("/api/v2/queue-health/es-index/");
export interface MemberIdentity {
	member_id: number;
	name: string | null;
	url: string | null;
}

export interface CookieMismatch {
	username: string;
	bot_id: number;
	session_id: number;
	expected: MemberIdentity;
	li_at: MemberIdentity;
	verification: "mismatch" | "enterprise_mismatch";
}

export interface CookieMismatchHealth {
	count: number;
	mismatches: CookieMismatch[];
}

export const fetchCookieMismatchHealth = (): Promise<CookieMismatchHealth> =>
	fetchJson<CookieMismatchHealth>("/api/v2/queue-health/cookie-mismatch/");

/** Banner alerts this admin has muted. Keyed on the check, not the message. */
export interface HealthAlertSnoozes {
	snoozed: string[];
}

export const fetchHealthAlertSnoozes = (): Promise<HealthAlertSnoozes> =>
	fetchJson<HealthAlertSnoozes>("/api/v2/queue-health/snooze/");

export const snoozeHealthAlert = async (alertKey: string): Promise<void> => {
	const response = await fetch("/api/v2/queue-health/snooze/", {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ alert_key: alertKey }),
	});
	if (!response.ok) {
		throw new Error(`Failed to snooze ${alertKey}: ${response.statusText}`);
	}
};
